import { useState } from 'react';

import { Box } from 'ui/box';

import Modal from 'components/partials/modal/modal';
import { Loader } from 'ui/loader';
import FavoriteReportTile from '../farovite-report-tile/farovite-report-tile';

import type { JsonNode } from 'api/generated/schemas';

import { useCurrentUser } from 'hooks/api/use-current-user';
import {
  useFavoriteReports,
  useUpdateFavoriteReport,
} from 'hooks/api/use-favorite-reports';

import useProductTypesDictionary from '../../use-product-types-dictionary';

import type { FavoriteReportData } from '../farovite-report-tile/farovite-report-tile';
import type { FavoriteReportFilter } from '../filter-item/filter-item';

interface FavoriteMetadata extends JsonNode {
  filters?: FavoriteReportFilter[];
}

interface UpdateModalContentProps {
  onClose: () => void;
  bookmarkState?: string | null;
  metadata?: FavoriteMetadata | null;
  reportId: string | number;
}

const UpdateModalContent = ({
  onClose,
  bookmarkState,
  metadata = null,
  reportId,
}: UpdateModalContentProps): React.JSX.Element => {
  const currentUser = useCurrentUser();
  const [selectedFavoriteReport, setSelectedFavoriteReport] =
    useState<FavoriteReportData | null>(null);
  const { dictionary: productTypesDictionary } = useProductTypesDictionary();
  const { data: favoriteReports, loading } = useFavoriteReports();
  const updateMutation = useUpdateFavoriteReport();

  const favoriteReportsList = favoriteReports as
    | FavoriteReportData[]
    | null
    | undefined;

  const handleClose = (): void => {
    setSelectedFavoriteReport(null);
    onClose();
  };

  const handleSubmitUpdate = async (): Promise<void> => {
    if (!bookmarkState) return;

    await updateMutation.mutateAsync({
      oktaUserId: currentUser.data!.oktaUserId,
      userId: Number(currentUser.data!.id),
      bookmarkId: Number(selectedFavoriteReport!.id),
      data: {
        reportId: Number(reportId),
        name: selectedFavoriteReport!.name,
        bookmarkState,
        metadata: metadata ?? undefined,
      },
    });

    handleClose();
  };

  return (
    <Modal
      headerText="Select favorite item to update"
      modalClassName="!max-w-[950px] !w-[950px] min-h-[360px]"
      onConfirmButtonLabel="Submit"
      onConfirmCallback={handleSubmitUpdate}
      onCloseCallback={handleClose}
      disabled={!selectedFavoriteReport}
      loading={updateMutation.isPending}
    >
      {loading ? (
        <Loader loading backdrop="transparent" />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 2,
            mb: 3,
            mt: 2,
            maxHeight: '420px',
            overflowY: 'auto',
            '@media (min-width: 1440px)': {
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            },
          }}
        >
          {favoriteReportsList?.map((favoriteReport) => (
            <FavoriteReportTile
              key={favoriteReport.id}
              favoriteReport={favoriteReport}
              productTypesDictionary={productTypesDictionary}
              showActions={false}
              isSelected={selectedFavoriteReport?.id === favoriteReport.id}
              onClick={() => setSelectedFavoriteReport(favoriteReport)}
            />
          ))}
        </Box>
      )}
    </Modal>
  );
};

export default UpdateModalContent;
